import { useEffect, useRef, useState } from 'react';

/**
 * Type-ahead select. The parent supplies `loadOptions(query)` which returns
 * `[{ id, label, sublabel? }]`; typing anything (code, name, email, …) filters
 * server- or client-side depending on that function. Selection is by id.
 */
export function SearchableSelect({
  value,
  onChange,
  loadOptions,
  placeholder,
  emptyText = 'Keine Treffer',
  loadingText = '…',
  disabled = false,
  selectedOption = null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [selected, setSelected] = useState(selectedOption);
  const rootRef = useRef(null);
  // Keep the latest loader without making it an effect dependency (inline
  // arrow functions from the parent would otherwise re-fire on every render).
  const loadRef = useRef(loadOptions);
  loadRef.current = loadOptions;

  // Mirror the controlled value into the displayed label.
  useEffect(() => {
    if (!value) {
      setSelected(null);
    } else if (selectedOption && selectedOption.id === value) {
      setSelected(selectedOption);
    }
  }, [value, selectedOption]);

  // Debounced option loading while the menu is open.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    const handle = setTimeout(() => {
      Promise.resolve(loadRef.current(query.trim()))
        .then((opts) => {
          if (!active) return;
          setOptions(Array.isArray(opts) ? opts : []);
          setHighlight(0);
        })
        .catch(() => {
          if (active) setOptions([]);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 180);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [open, query]);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return undefined;
    function onDocMouseDown(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  function choose(option) {
    setSelected(option);
    setOpen(false);
    setQuery('');
    onChange(option.id, option);
  }

  function handleKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (event.key === 'Enter') {
      if (open && options[highlight]) {
        event.preventDefault();
        choose(options[highlight]);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }

  return (
    <div className="searchable-select" ref={rootRef}>
      <input
        className="input"
        type="text"
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={open ? query : selected?.label ?? ''}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          if (!open) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />
      {open && (
        <ul className="searchable-select-menu" role="listbox">
          {loading && <li className="searchable-select-empty">{loadingText}</li>}
          {!loading && options.length === 0 && (
            <li className="searchable-select-empty">{emptyText}</li>
          )}
          {!loading &&
            options.map((option, index) => (
              <li
                key={option.id}
                role="option"
                aria-selected={option.id === value}
                className={`searchable-select-option${index === highlight ? ' is-active' : ''}`}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(option);
                }}
              >
                <span className="searchable-select-option-label">{option.label}</span>
                {option.sublabel && (
                  <span className="searchable-select-option-sub">{option.sublabel}</span>
                )}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
